import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';
import { SESSION_STATUSES, BOOKING_FEE_DISPLAY } from '../utils/constants';
import { formatDate, formatTime } from '../utils/timeFormat';
import RatingModal from '../components/RatingModal';
import './DashboardPage.css';

function isPast(dateStr, timeStr) {
  if (!dateStr) return false;
  return new Date(`${dateStr}T${timeStr || '23:59'}`) < new Date();
}

function isHistorySession(req) {
  return (
    req.status === SESSION_STATUSES.COMPLETED ||
    req.status === SESSION_STATUSES.DECLINED ||
    req.status === SESSION_STATUSES.CANCELLED ||
    (req.status === SESSION_STATUSES.PAID && isPast(req.proposed_date, req.proposed_time))
  );
}

function getDisplayStatus(req) {
  if (req.status === SESSION_STATUSES.PAID && !isPast(req.proposed_date, req.proposed_time)) return 'scheduled';
  if (req.status === SESSION_STATUSES.PAID && isPast(req.proposed_date, req.proposed_time)) return 'completed';
  return req.status;
}

function getSessionTotal(req) {
  if (!req.trainer?.hourly_rate || !req.duration) return null;
  return (Number(req.trainer.hourly_rate) * Number(req.duration)).toFixed(0);
}

function StatusBadge({ status }) {
  const labels = {
    pending: 'Pending',
    accepted: 'Accepted',
    scheduled: 'Scheduled',
    completed: 'Completed',
    declined: 'Declined',
    cancelled: 'Cancelled',
    change_suggested: 'Change Suggested',
  };
  return (
    <span className={`dp-badge dp-badge-${status}`}>
      {labels[status] || status}
    </span>
  );
}

function ConfirmForm({ message, placeholder, confirmLabel, confirmClass, onConfirm, onBack }) {
  const [reason, setReason] = React.useState('');
  return (
    <div className="dp-confirm-form">
      <p className="dp-confirm-form-msg">{message}</p>
      <textarea
        className="dp-confirm-form-textarea"
        value={reason}
        onChange={e => setReason(e.target.value)}
        placeholder={placeholder}
        rows={2}
        maxLength={200}
      />
      <div className="dp-confirm-form-actions">
        <button className={`dp-confirm-btn ${confirmClass}`} onClick={() => onConfirm(reason)}>{confirmLabel}</button>
        <button className="dp-btn-ghost" onClick={onBack}>Go Back</button>
      </div>
    </div>
  );
}

function SessionCard({ request, onCancel, onAcceptChange, onDeclineChange, onRateClick, isHistory }) {
  const displayStatus = getDisplayStatus(request);
  const [showCancelForm, setShowCancelForm] = React.useState(false);
  const sessionTotal = getSessionTotal(request);
  const date = request.suggested_date || request.proposed_date;
  const time = request.suggested_time || request.proposed_time;
  const location = request.suggested_location || request.proposed_location;
  const hasReviewed = request.ratings && request.ratings.length > 0;
  const isScheduled = request.status === SESSION_STATUSES.PAID && !isPast(request.proposed_date, request.proposed_time);
  const isPastPaid = request.status === SESSION_STATUSES.PAID && isPast(request.proposed_date, request.proposed_time);
  const isDone = request.status === SESSION_STATUSES.COMPLETED || isPastPaid;

  return (
    <div className={`dp-card${isHistory ? ' dp-card-history' : ''}`}>
      <div className={`dp-card-accent dp-accent-${displayStatus}`} />
      <div className="dp-card-body">

        <div className="dp-card-head">
          <div className="dp-trainer-row">
            {request.trainer?.avatar_url ? (
              <img src={request.trainer.avatar_url} alt="" className="dp-avatar" />
            ) : (
              <div className="dp-avatar-ph">
                {request.trainer?.first_name?.[0]?.toUpperCase() || 'T'}
              </div>
            )}
            <div>
              <Link to={`/mentors/${request.mentor_id}`} className="dp-trainer-name">
                {request.trainer ? `${request.trainer.first_name} ${request.trainer.last_name}` : 'Your Trainer'}
              </Link>
              <StatusBadge status={displayStatus} />
            </div>
          </div>
        </div>

        {request.status === SESSION_STATUSES.CHANGE_SUGGESTED && request.suggested_date && (
          <div className="dp-change-compare">
            <div className="dp-change-col">
              <div className="dp-change-col-label">Your Original</div>
              <p>{formatDate(request.proposed_date)}</p>
              <p>{formatTime(request.proposed_time)}</p>
              <p>{request.proposed_location}</p>
            </div>
            <div className="dp-change-arrow">→</div>
            <div className="dp-change-col dp-change-col-new">
              <div className="dp-change-col-label">Trainer Suggests</div>
              <p>{formatDate(request.suggested_date || request.proposed_date)}</p>
              <p>{formatTime(request.suggested_time || request.proposed_time)}</p>
              <p>{request.suggested_location || request.proposed_location}</p>
            </div>
          </div>
        )}
        {request.status === SESSION_STATUSES.CHANGE_SUGGESTED && request.change_reason && (
          <div className="dp-change-reason">
            <strong>Reason:</strong> {request.change_reason}
            {request.change_note && <span> — "{request.change_note}"</span>}
          </div>
        )}

        <div className="dp-detail-grid">
          <div className="dp-detail-cell">
            <div className="dp-dk">Sport</div>
            <div className="dp-dv">{request.sport}</div>
          </div>
          <div className="dp-detail-cell">
            <div className="dp-dk">Date</div>
            <div className="dp-dv">{formatDate(date)}</div>
          </div>
          <div className="dp-detail-cell">
            <div className="dp-dk">Time</div>
            <div className="dp-dv">{formatTime(time)}</div>
          </div>
          <div className="dp-detail-cell">
            <div className="dp-dk">Location</div>
            <div className="dp-dv">{location}</div>
          </div>
          {request.duration && (
            <div className="dp-detail-cell">
              <div className="dp-dk">Duration</div>
              <div className="dp-dv">{request.duration} {Number(request.duration) === 1 ? 'hour' : 'hours'}</div>
            </div>
          )}
          {request.child_age_range && (
            <div className="dp-detail-cell">
              <div className="dp-dk">Athlete Age</div>
              <div className="dp-dv">{request.child_age_range} yrs</div>
            </div>
          )}
          {sessionTotal && (
            <div className="dp-detail-cell">
              <div className="dp-dk">Session Total</div>
              <div className="dp-dv">${sessionTotal}</div>
            </div>
          )}
          {request.notes && (
            <div className="dp-detail-cell dp-detail-cell-full">
              <div className="dp-dk">Notes</div>
              <div className="dp-dv">{request.notes}</div>
            </div>
          )}
        </div>

        <div className="dp-card-foot">
          <div className="dp-foot-msg">
            {request.status === SESSION_STATUSES.PENDING && 'Awaiting trainer response'}
            {request.status === SESSION_STATUSES.ACCEPTED && 'Pay $4 to confirm your spot'}
            {isScheduled && <span className="dp-foot-msg-green">Remember to pay your trainer in person</span>}
            {request.status === SESSION_STATUSES.DECLINED && (
              <span>{request.decline_reason ? `Trainer declined: "${request.decline_reason}"` : 'Trainer declined this request'}</span>
            )}
            {request.status === SESSION_STATUSES.CANCELLED && (
              <span>{request.cancel_reason ? `Cancelled: "${request.cancel_reason}"` : 'Session cancelled'}</span>
            )}
            {isDone && !hasReviewed && 'Session completed'}
            {isDone && hasReviewed && <span className="dp-foot-msg-reviewed">Reviewed</span>}
            {request.status === SESSION_STATUSES.CHANGE_SUGGESTED && 'Review the suggested changes above'}
          </div>

          <div className="dp-actions">
            {request.status === SESSION_STATUSES.CHANGE_SUGGESTED && (
              <>
                <button className="dp-btn-secondary" onClick={() => onAcceptChange(request.id)}>Accept Change</button>
                <button className="dp-btn-danger" onClick={() => onDeclineChange(request.id)}>Decline</button>
              </>
            )}
            {request.status === SESSION_STATUSES.PENDING && !showCancelForm && (
              <button className="dp-btn-ghost" onClick={() => setShowCancelForm(true)}>Cancel</button>
            )}
            {request.status === SESSION_STATUSES.ACCEPTED && !showCancelForm && (
              <>
                <button className="dp-btn-ghost" onClick={() => setShowCancelForm(true)}>Cancel</button>
                <Link to={`/payment/${request.id}`} className="dp-btn-primary">Pay {BOOKING_FEE_DISPLAY}</Link>
              </>
            )}
            {isScheduled && !showCancelForm && (
              <button className="dp-btn-ghost" onClick={() => setShowCancelForm(true)}>Cancel Session</button>
            )}
            {isDone && !hasReviewed && (
              <button className="dp-btn-review" onClick={() => onRateClick(request)}>Leave a Review</button>
            )}
          </div>
          {showCancelForm && (
            <ConfirmForm
              message="Are you sure you want to cancel this session?"
              placeholder="Reason for cancelling (optional)"
              confirmLabel="Confirm Cancellation"
              confirmClass="dp-btn-danger-confirm"
              onConfirm={(reason) => { onCancel(request.id, reason); setShowCancelForm(false); }}
              onBack={() => setShowCancelForm(false)}
            />
          )}
        </div>

      </div>
    </div>
  );
}

function StatsBar({ requests, onToReviewClick }) {
  const upcoming = requests.filter(r => r.status === SESSION_STATUSES.PAID && !isPast(r.proposed_date, r.proposed_time)).length;
  const pending = requests.filter(r => r.status === SESSION_STATUSES.PENDING).length;
  const completed = requests.filter(r =>
    r.status === SESSION_STATUSES.COMPLETED ||
    (r.status === SESSION_STATUSES.PAID && isPast(r.proposed_date, r.proposed_time))
  ).length;
  const toReview = requests.filter(r => {
    const isDone = r.status === SESSION_STATUSES.COMPLETED || (r.status === SESSION_STATUSES.PAID && isPast(r.proposed_date, r.proposed_time));
    return isDone && !(r.ratings && r.ratings.length > 0);
  }).length;

  return (
    <div className="dp-stats">
      <div className="dp-stat">
        <div className="dp-stat-label">Upcoming</div>
        <div className="dp-stat-num dp-stat-green">{upcoming}</div>
      </div>
      <div className="dp-stat">
        <div className="dp-stat-label">Pending</div>
        <div className="dp-stat-num dp-stat-amber">{pending}</div>
      </div>
      <div className="dp-stat">
        <div className="dp-stat-label">Completed</div>
        <div className="dp-stat-num">{completed}</div>
      </div>
      <div
        className={`dp-stat${toReview > 0 ? ' dp-stat-highlight' : ''}`}
        onClick={toReview > 0 ? onToReviewClick : undefined}
        style={toReview > 0 ? { cursor: 'pointer' } : {}}
      >
        <div className="dp-stat-label">To Review</div>
        <div className="dp-stat-num dp-stat-purple">{toReview}</div>
      </div>
    </div>
  );
}

function InterestsSection({ profile }) {
  const sports = profile?.sports_interest || [];
  const areas = profile?.focus_areas_interest || [];

  if (!sports.length && !areas.length) return null;

  return (
    <div className="dp-interests">
      <div className="dp-interests-head">
        <span className="dp-interests-title">Your Child's Interests</span>
        <Link to="/profile" className="dp-interests-edit">Edit interests</Link>
      </div>
      {sports.length > 0 && (
        <div className="dp-interest-group">
          <div className="dp-interest-label">Sports</div>
          <div className="dp-tags">
            {sports.map(s => <span key={s} className="dp-tag">{s}</span>)}
          </div>
        </div>
      )}
      {areas.length > 0 && (
        <div className="dp-interest-group">
          <div className="dp-interest-label">Areas to Improve</div>
          <div className="dp-tags">
            {areas.map(a => <span key={a} className="dp-tag">{a}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

function DashboardPage() {
  const { user, profile } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [reviewFilter, setReviewFilter] = useState(false);
  const [ratingTarget, setRatingTarget] = useState(null);

  const fetchRequests = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const queryPromise = supabase
        .from('session_requests')
        .select(`
          *,
          ratings(id),
          trainer:profiles!session_requests_mentor_id_fkey(id, first_name, last_name, avatar_url, hourly_rate)
        `)
        .eq('parent_id', user.id)
        .order('proposed_date', { ascending: true });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Please try again.')), 10000)
      );

      const result = await Promise.race([queryPromise, timeoutPromise]);
      const { data, error: err } = result;
      if (err) throw err;
      setRequests(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load your sessions.');
      if (process.env.NODE_ENV === 'development') {
        console.error('Dashboard fetch error:', err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchRequests();
  }, [fetchRequests, user]);

  const handleCancel = async (requestId, reason) => {
    setActionError('');
    try {
      const { error } = await supabase
        .from('session_requests')
        .update({ status: SESSION_STATUSES.CANCELLED, cancel_reason: reason || null })
        .eq('id', requestId)
        .eq('parent_id', user.id);
      if (error) throw error;
      await fetchRequests();
    } catch (err) {
      setActionError('Failed to cancel. Please try again.');
    }
  };

  const handleAcceptChange = async (requestId) => {
    setActionError('');
    try {
      const { error } = await supabase
        .from('session_requests')
        .update({ status: SESSION_STATUSES.ACCEPTED })
        .eq('id', requestId)
        .eq('parent_id', user.id);
      if (error) throw error;
      await fetchRequests();
    } catch (err) {
      setActionError('Failed to accept change. Please try again.');
    }
  };

  const handleDeclineChange = async (requestId) => {
    setActionError('');
    try {
      const { error } = await supabase
        .from('session_requests')
        .update({ status: SESSION_STATUSES.DECLINED })
        .eq('id', requestId)
        .eq('parent_id', user.id);
      if (error) throw error;
      await fetchRequests();
    } catch (err) {
      setActionError('Failed to decline. Please try again.');
    }
  };

  const handleRatingSubmitted = () => {
    setRatingTarget(null);
    fetchRequests();
  };

  const handleToReviewClick = () => {
    setActiveTab('history');
    setReviewFilter(true);
  };

  const activeRequests = requests.filter(r => !isHistorySession(r));
  const historyRequests = requests.filter(r => isHistorySession(r));
  const filteredHistory = reviewFilter
    ? historyRequests.filter(r => {
        const actuallyHappened = r.status === SESSION_STATUSES.COMPLETED || (r.status === SESSION_STATUSES.PAID && isPast(r.proposed_date, r.proposed_time));
        return actuallyHappened && !(r.ratings && r.ratings.length > 0);
      })
    : historyRequests;
  const toReviewCount = historyRequests.filter(r => {
    const actuallyHappened = r.status === SESSION_STATUSES.COMPLETED || (r.status === SESSION_STATUSES.PAID && isPast(r.proposed_date, r.proposed_time));
    return actuallyHappened && !(r.ratings && r.ratings.length > 0);
  }).length;

  return (
    <div className="dp-wrapper">
    <div className="page-container dp-page">
      <div className="dp-header">
        <div>
          <h1 className="dp-title">My Sessions</h1>
          <p className="dp-subtitle">Welcome back, {profile?.first_name || 'there'}</p>
        </div>
        <Link to="/mentors" className="btn btn-primary">+ Find a Trainer</Link>
      </div>

      {actionError && <div className="alert alert-error">{actionError}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="app-loading">
          <div className="app-loading-spinner" />
          <p style={{ marginTop: '0.75rem', color: '#8aaec8', fontFamily: 'Open Sans, sans-serif' }}>Loading your sessions…</p>
        </div>
      ) : (
        <>
          <StatsBar requests={requests} onToReviewClick={handleToReviewClick} />
          <InterestsSection profile={profile} />

          <div className="dp-tabs">
            <button
              className={`dp-tab${activeTab === 'active' ? ' dp-tab-active' : ''}`}
              onClick={() => { setActiveTab('active'); setReviewFilter(false); }}
            >
              Active <span className="dp-tab-badge">{activeRequests.length}</span>
            </button>
            <button
              className={`dp-tab${activeTab === 'history' ? ' dp-tab-active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              History <span className="dp-tab-badge">{historyRequests.length}</span>
            </button>
          </div>

          {activeTab === 'active' && (
            activeRequests.length === 0 ? (
              <div className="dp-empty">
                <p>No active sessions yet.</p>
                <Link to="/mentors" className="btn btn-primary" style={{ marginTop: '1rem' }}>Find a Trainer</Link>
              </div>
            ) : (
              activeRequests.map(req => (
                <SessionCard
                  key={req.id}
                  request={req}
                  onCancel={handleCancel}
                  onAcceptChange={handleAcceptChange}
                  onDeclineChange={handleDeclineChange}
                  onRateClick={setRatingTarget}
                  isHistory={false}
                />
              ))
            )
          )}

          {activeTab === 'history' && (
            <>
              {toReviewCount > 0 && (
                <div className="dp-filter-bar">
                  <button
                    className={`dp-filter-btn${reviewFilter ? ' dp-filter-btn-active' : ''}`}
                    onClick={() => setReviewFilter(f => !f)}
                  >
                    Needs Review ({toReviewCount})
                  </button>
                </div>
              )}
              {filteredHistory.length === 0 ? (
                <div className="dp-empty"><p>No past sessions{reviewFilter ? ' needing a review' : ''}.</p></div>
              ) : (
                filteredHistory.map(req => (
                  <SessionCard
                    key={req.id}
                    request={req}
                    onCancel={handleCancel}
                    onAcceptChange={handleAcceptChange}
                    onDeclineChange={handleDeclineChange}
                    onRateClick={setRatingTarget}
                    isHistory={true}
                  />
                ))
              )}
            </>
          )}
        </>
      )}

      {ratingTarget && (
        <RatingModal
          request={ratingTarget}
          onClose={() => setRatingTarget(null)}
          onSubmitted={handleRatingSubmitted}
        />
      )}
    </div>
    </div>
  );
}

export default DashboardPage;
